/**
 * Validation utility functions and Zod schemas
 */
import { z } from 'zod';
import { PaymentMethod } from '@/types/order.types';

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validate Indian phone number (10 digits starting with 6-9)
 */
export const isValidPhoneNumber = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  return /^[6-9]\d{9}$/.test(cleaned);
};

/**
 * Validate email address
 */
export const isValidEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * Validate Indian pincode (6 digits, first digit not 0)
 */
export const isValidPincode = (pincode: string): boolean => {
  return /^[1-9][0-9]{5}$/.test(pincode);
};

/**
 * Validate GST number
 */
export const isValidGSTNumber = (gst: string): boolean => {
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);
};

/**
 * Validate PAN number
 */
export const isValidPAN = (pan: string): boolean => {
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan);
};

/**
 * Check if string is empty or whitespace only
 */
export const isEmpty = (str: string | null | undefined): boolean => {
  return !str || str.trim().length === 0;
};

/**
 * Validate URL
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ============================================
// ZOD SCHEMAS
// ============================================

/**
 * Phone number schema
 */
export const phoneSchema = z
  .string()
  .min(10, 'Phone number must be 10 digits')
  .max(10, 'Phone number must be 10 digits')
  .regex(/^[6-9]\d{9}$/, 'Invalid Indian phone number');

/**
 * Email schema
 */
export const emailSchema = z.string().email('Invalid email address');

/**
 * Optional email schema - allows empty string or valid email
 */
export const optionalEmailSchema = z
  .string()
  .refine(
    (val) => val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
    { message: 'Invalid email address' }
  )
  .optional();

/**
 * Pincode schema
 */
export const pincodeSchema = z
  .string()
  .regex(/^[1-9][0-9]{5}$/, 'Invalid pincode');

/**
 * Address schema
 */
export const addressSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  phoneNumber: phoneSchema,
  addressLine1: z.string().min(10, 'Address must be at least 10 characters'),
  addressLine2: z.string().optional(),
  landmark: z.string().optional(),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'State is required'),
  pincode: pincodeSchema,
  isDefault: z.boolean().default(false),
});

/**
 * Login schema
 */
export const loginSchema = z.object({
  phoneNumber: phoneSchema,
});

/**
 * OTP schema
 */
export const otpSchema = z.object({
  otp: z
    .string()
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d+$/, 'OTP must contain only numbers'),
});

/**
 * Profile update schema
 */
export const profileSchema = z.object({
  displayName: z.string().min(2, 'Name must be at least 2 characters'),
  email: optionalEmailSchema,
});

/**
 * Guest checkout schema
 */
export const guestCheckoutSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  email: emailSchema,
  phoneNumber: phoneSchema,
});

/**
 * Contact form schema
 */
export const contactFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: emailSchema,
  phone: phoneSchema,
  subject: z.string().min(5, 'Subject must be at least 5 characters'),
  message: z.string().min(20, 'Message must be at least 20 characters'),
});

/**
 * Product form schema
 */
export const productFormSchema = z.object({
  name: z.string().min(3, 'Name must be at least 3 characters'),
  description: z.string().min(20, 'Description must be at least 20 characters'),
  shortDescription: z.string().optional(),
  price: z.number().min(1, 'Price must be greater than 0'),
  originalPrice: z.number().optional(),
  category: z.string().min(1, 'Category is required'),
  brand: z.string().min(1, 'Brand is required'),
  moq: z.number().min(1, 'MOQ must be at least 1'),
  stockQuantity: z.number().min(0, 'Stock cannot be negative'),
  sku: z.string().min(3, 'SKU must be at least 3 characters'),
  isFeatured: z.boolean().default(false),
  isActive: z.boolean().default(true),
});

/**
 * Checkout form schema
 */
export const checkoutFormSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  phoneNumber: phoneSchema,
  email: z
    .string()
    .optional()
    .refine(
      (val) => !val || val === '' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
      { message: 'Invalid email address' }
    ),
  addressLine1: z.string().min(10, 'Address must be at least 10 characters'),
  addressLine2: z.string().optional(),
  landmark: z.string().optional(),
  city: z.string().min(2, 'City is required'),
  state: z.string().min(2, 'Please select a state'),
  pincode: pincodeSchema,
  notes: z.string().optional(),
  paymentMethod: z.nativeEnum(PaymentMethod).default(PaymentMethod.COD),
  saveAddress: z.boolean().default(true),
});

/**
 * Track order schema (guest order lookup)
 */
export const trackOrderSchema = z.object({
  orderNumber: z
    .string()
    .min(1, 'Order number is required')
    .regex(/^SV-/, 'Order number must start with SV-'),
  phoneNumber: phoneSchema,
});

// ============================================
// TYPE EXPORTS
// ============================================

export type AddressFormData = z.infer<typeof addressSchema>;
export type LoginFormData = z.infer<typeof loginSchema>;
export type OtpFormData = z.infer<typeof otpSchema>;
export type ProfileFormData = z.infer<typeof profileSchema>;
export type GuestCheckoutFormData = z.infer<typeof guestCheckoutSchema>;
export type ContactFormData = z.infer<typeof contactFormSchema>;
export type ProductFormData = z.infer<typeof productFormSchema>;
export type CheckoutFormData = z.infer<typeof checkoutFormSchema>;
export type TrackOrderFormData = z.infer<typeof trackOrderSchema>;
